// apps/workflow/src/pages/freelancer/Subscribe.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const FreelancerSubscribe = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [checking, setChecking] = useState(true);

  // ─── Refresh user on mount and check role/subscription ────
  useEffect(() => {
    const checkUser = async () => {
      if (user) {
        // Refresh user data to get latest role and subscription
        await refreshUser();
        const isFreelancer = user.role === 'freelancer';
        const hasActive = user.subscriptionEnd && new Date() < new Date(user.subscriptionEnd);
        // If already has active subscription or not a freelancer, redirect to dashboard
        if (!isFreelancer || hasActive) {
          navigate('/designer/dashboard');
          return;
        }
      }
      setChecking(false);
    };
    checkUser();
  }, [user, refreshUser, navigate]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Create Razorpay order
      const orderRes = await fetch('http://localhost:5001/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: 500,
          currency: 'INR',
          receipt: `sub_${user.id}_${Date.now()}`,
        }),
      });
      const orderData = await orderRes.json();
      if (!orderData.success) throw new Error(orderData.error || 'Order creation failed');

      const keyRes = await fetch('http://localhost:5001/api/get-key');
      const keyData = await keyRes.json();
      if (!keyData.key) throw new Error('Razorpay key not configured');

      const options = {
        key: keyData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'HouseDesigns99',
        description: 'Freelancer Subscription (1 year)',
        order_id: orderData.orderId,
        handler: async function (paymentResponse) {
          const verifyRes = await fetch('http://localhost:5001/api/verify-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            const now = new Date();
            const end = new Date(now);
            end.setFullYear(end.getFullYear() + 1);
            await pb.collection('users').update(user.id, {
              subscriptionStart: now.toISOString(),
              subscriptionEnd: end.toISOString(),
            });
            await refreshUser();
            setSuccess(true);
            toast.success('Subscription activated! Valid until ' + end.toLocaleDateString());
            setTimeout(() => navigate('/designer/dashboard'), 2000);
          } else {
            toast.error('Payment verification failed');
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            toast.info('Payment cancelled');
            setLoading(false);
          },
        },
        prefill: {
          name: user.name,
          email: user.email,
        },
        theme: { color: '#f59e0b' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error(error);
      toast.error('Payment initiation failed: ' + error.message);
      setLoading(false);
    }
  };

  if (checking) {
    return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>;
  }

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
        <h2 className="text-2xl font-bold text-slate-800">Subscription Activated!</h2>
        <p className="text-slate-600">You now have full access to the freelancer dashboard.</p>
        <button onClick={() => navigate('/designer/dashboard')} className="mt-4 text-amber-600 hover:underline">
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-12 p-6 bg-white rounded-xl shadow-sm border border-slate-200">
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Activate Freelancer Subscription</h1>
      <p className="text-slate-500 mb-6">Pay ₹500 to unlock full access for 1 year.</p>
      <div className="bg-slate-50 p-4 rounded-lg mb-6">
        <div className="flex justify-between">
          <span className="text-slate-600">Status</span>
          <span className="font-medium text-amber-600">Not Active</span>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-slate-600">Price</span>
          <span className="font-medium">₹500 / year</span>
        </div>
      </div>
      <button
        onClick={handleSubscribe}
        disabled={loading}
        className="w-full py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
        {loading ? 'Processing...' : 'Subscribe Now – ₹500'}
      </button>
    </div>
  );
};

export default FreelancerSubscribe;